const BASE_URL = import.meta.env.VITE_UMPIRE_URL ?? "http://localhost:8080";

// --- Types ---

export interface PlayerDto {
  name: string;
  role: string;
}

export interface CreateGameRequest {
  allegiance: string;
  players: PlayerDto[];
}

export interface JoinGameRequest {
  joinCode: string;
  allegiance: string;
  players: PlayerDto[];
}

export interface CreateGameResponse {
  gameId: string;
  joinCode: string;
  homeToken: string;
}

export interface JoinGameResponse {
  awayToken: string;
}

export interface SpectateGameResponse {
  spectatorToken: string;
  phase: string;
}

export interface GameStatusResponse {
  gameId: string;
  phase: string;
}

export interface GameSummaryResponse {
  gameId: string;
  status: "WAITING_FOR_OPPONENT" | "READY" | "IN_PROGRESS" | "FINISHED";
}

export interface GameListResponse {
  games: GameSummaryResponse[];
}

// --- API ---

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

/** GET /api/games — list all games */
export function listGames(): Promise<GameListResponse> {
  return request<GameListResponse>("/api/games");
}

/** POST /api/games — create a new game */
export function createGame(body: CreateGameRequest): Promise<CreateGameResponse> {
  return request<CreateGameResponse>("/api/games", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** GET /api/games/:id — get game status */
export function getGameStatus(gameId: string): Promise<GameStatusResponse> {
  return request<GameStatusResponse>(`/api/games/${gameId}`);
}

/** POST /api/games/:id/join — join a game */
export function joinGame(gameId: string, body: JoinGameRequest): Promise<JoinGameResponse> {
  return request<JoinGameResponse>(`/api/games/${gameId}/join`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** POST /api/games/:id/spectate — spectate a game */
export function spectateGame(gameId: string): Promise<SpectateGameResponse> {
  return request<SpectateGameResponse>(`/api/games/${gameId}/spectate`, {
    method: "POST",
  });
}
